import { useGifNotification } from "@features/notification/useGifNotification";
import React, { useCallback, useEffect, useState } from "react";
import { GifUrl } from "@shared/types";
import "./styles.css";
import Button from "react-bootstrap/Button";
import { Form, Gif, currentTime, FetchGifParams } from "@features/collage";
import Stack from "react-bootstrap/Stack";
import Modal from "./modal";

const MAX_ITEM_COUNT = 10;
const ARROW_CHAR_CODE = String.fromCharCode(8594);

const SERVERLESS_URL =
  process.env.REACT_APP_SERVERLESS_URL || "http://localhost:7071/api";

interface GifLastPostedState {
  [key: string]: number;
}

const Collage: React.FC = () => {
  const [gifLastPosted, setGifLastPosted] = useState<GifLastPostedState>({});
  const [gifUrls, setGifUrls] = useState<GifUrl[]>([]);
  const [continuationToken, setToken] = useState<string>();

  const gifHanlder = (url: GifUrl) => {
    setGifUrls((urls) => [...urls, url]);
    setGifLastPosted((state: GifLastPostedState) => ({
      ...state,
      [url.id]: currentTime(),
    }));
  };

  useGifNotification({
    handler: gifHanlder,
  });

  const fetchGifs = useCallback(
    async (
      { maxItemCount, continuationToken }: FetchGifParams = {
        maxItemCount: MAX_ITEM_COUNT,
      }
    ) => {
      const params = new URLSearchParams({
        maxItemCount: maxItemCount.toString(),
        ...(continuationToken && { continuationToken }),
      });
      const response = await fetch(
        `${SERVERLESS_URL}/getGifCollageState?${params}`,
        {
          mode: "cors",
        }
      );
      const { gifUrls, continuationToken: tokenReceived } =
        await response.json();
      setToken(tokenReceived);
      if (gifUrls)
        setGifUrls((state) => [
          ...state,
          ...gifUrls.filter(({ url }: GifUrl) => Boolean(url)),
        ]);
    },
    []
  );

  useEffect(() => {
    fetchGifs();
  }, [fetchGifs]);

  const onPageNext = () => {
    fetchGifs({
      continuationToken,
      maxItemCount: MAX_ITEM_COUNT,
    });
  };

  const onClear = () => {
    setGifUrls([]);
  };

  const onAnimationEnd = (id: string) => {
    setGifUrls((giflUrls) => giflUrls.filter((gif) => gif.id !== id));
  };

  const isDisabled = !Boolean(continuationToken) && Boolean(gifUrls.length);

  return (
    <div className="main">
      <Stack direction="horizontal" gap={3}>
        <Modal className="bm-wrap" label={"Add Gif"}>
          <Form className="menu-item" />
        </Modal>
        <Button
          variant="dark"
          className="mx-2 my-2 button page-clear"
          onClick={onClear}
        >
          Clear
        </Button>
      </Stack>
      {gifUrls.map((gif) => (
        <Gif
          {...gif}
          key={gif.id}
          onAnimationEnd={onAnimationEnd}
          lastPosted={gifLastPosted[gif.id]}
        />
      ))}
      <Button
        variant="dark"
        className="button page-next"
        disabled={isDisabled}
        onClick={onPageNext}
      >
        {ARROW_CHAR_CODE}
      </Button>
    </div>
  );
};

export default Collage;
